import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";

export default function WorkloadPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/workload`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);

  if (!data) return <div className="text-muted-foreground">Loading workload…</div>;
  const maxOpen = Math.max(...data.members.map((m) => m.open), 1);

  return (
    <div data-testid="workload-page">
      <PageHeader overline="Collaboration · Capacity" title="Resource Workload">
        <div className="text-xs text-muted-foreground mt-1">
          {data.totalTasks} tasks · {data.unassignedTasks} unassigned — assign owners on the Gantt to balance load.
        </div>
      </PageHeader>
      <div className="border border-border bg-card overflow-auto">
        <table className="w-full data-table text-sm">
          <thead>
            <tr><th>Member</th><th>Role</th><th>Accountable</th><th>Assigned</th><th>Open</th><th>Overdue</th><th>Done</th><th>Open effort</th><th>Capacity (h/wk)</th><th>Rate ($/h)</th><th>Demand (h/wk)</th><th style={{ minWidth: 200 }}>Load</th></tr>
          </thead>
          <tbody>
            {data.members.map((m) => {
              const heavy = m.open >= maxOpen && m.open > 0;
              return (
                <tr key={m.userId} data-testid="workload-row" className={m.overCapacity ? "!bg-[#FF3B30]/5" : ""}>
                  <td className="font-medium">{m.name}
                    {heavy && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#b3221a] font-bold">heaviest</span>}
                    {m.overCapacity && <span data-testid="over-capacity-flag" className="ml-2 text-[10px] uppercase tracking-wide bg-[#FF3B30] text-white px-1 py-0.5 font-bold">over capacity</span>}
                  </td>
                  <td><StatusBadge value={m.role} tone="neutral" /></td>
                  <td>{m.accountable}</td>
                  <td>{m.assigned}</td>
                  <td className={m.open > 0 ? "font-semibold" : ""}>{m.open}</td>
                  <td>{m.overdue > 0 ? <StatusBadge value={m.overdue} tone="red" /> : "0"}</td>
                  <td>{m.completed}</td>
                  <td>{m.openDays}d</td>
                  <td>
                    <input data-testid="capacity-input" type="number" min="0" max="100" defaultValue={m.weeklyHours || ""}
                      placeholder="—" onBlur={async (e) => {
                        const v = Number(e.target.value || 0);
                        if (v === (m.weeklyHours || 0)) return;
                        try {
                          await api.patch(`/projects/${projectId}/members/${m.memberId}`, { weeklyHours: v });
                          toast.success(`${m.name}: capacity set to ${v}h/week`);
                          api.get(`/projects/${projectId}/workload`).then((r) => setData(r.data));
                        } catch (err) { toast.error(errMsg(err)); }
                      }}
                      className="w-16 border border-input px-1.5 py-1 text-xs bg-background" />
                  </td>
                  <td>
                    <input data-testid="rate-input" type="number" min="0" max="10000" defaultValue={m.hourlyRate || ""}
                      placeholder="—" onBlur={async (e) => {
                        const v = Number(e.target.value || 0);
                        if (v === (m.hourlyRate || 0)) return;
                        try {
                          await api.patch(`/projects/${projectId}/members/${m.memberId}`, { hourlyRate: v });
                          toast.success(`${m.name}: rate set to $${v}/h — timesheet hours now cost into Finance`);
                          api.get(`/projects/${projectId}/workload`).then((r) => setData(r.data));
                        } catch (err) { toast.error(errMsg(err)); }
                      }}
                      className="w-16 border border-input px-1.5 py-1 text-xs bg-background" />
                  </td>
                  <td className={m.overCapacity ? "text-[#b3221a] font-bold" : ""}>{m.weeklyDemandHours}h</td>
                  <td>
                    <div className="h-3 bg-neutral-100 w-full relative">
                      <div className={`h-3 ${m.overCapacity || m.overdue > 0 ? "bg-[#FF3B30]" : "bg-[#1E6FE8]"}`}
                        style={{ width: `${m.weeklyHours > 0 ? Math.min((m.weeklyDemandHours / m.weeklyHours) * 100, 100) : (m.open / maxOpen) * 100}%` }} />
                      {m.weeklyHours > 0 && <div className="absolute inset-0 border-r-2 border-black/40" />}
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.members.length === 0 && <tr><td colSpan={12} className="py-6 text-center text-muted-foreground">No members — add some on Members & Roles.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
